import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, Brain, Lock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

const FOCUS_MODES = [
  { key: "leverage", label: "Leverage", desc: "Sub-5% pivots vs the field" },
  { key: "chalk",    label: "Chalk",    desc: "Confirm & fade the popular plays" },
  { key: "stacks",   label: "Stacks",   desc: "QB/game combos ranked by ceiling" },
  { key: "showdown", label: "Showdown", desc: "Captain picks + game correlation" },
  { key: "cash",     label: "Cash",     desc: "Floor-first roster construction" },
];

const SAMPLE_REPORT = `## Slate Attack Plan
This is a **sample** of the plan the AI slate brief delivers 30–90 seconds after you click Run.

## Chalk To Fade
- **Christian McCaffrey (RB, 32% own)** — Ceiling capped by projected 27-point favorite; punt to CMC pivots below.
- **Ja'Marr Chase (WR, 28% own)** — Everyone will have him; every uncontested Milly winner shipped ≤14% Chase exposure.

## Leverage To Attack
- **Marvin Harrison Jr (WR, 4% own)** — Vegas ITT 27.5, matchup vs 28th-ranked pass D, deep-shot alpha profile.
- **Sam LaPorta (TE, 3% own)** — 26% target share last 3 games; game total 51.5.

## Stack Combos
1. **Jayden Daniels + Terry McLaurin + Sam LaPorta bring-back** — 63.79% combined own barbell, projected ceiling 168 pts.
2. **Tua + Waddle + Achane FLEX + Bengals WR bring-back** — 5-man game stack, ITT 28.5/28.0.

## Roster Construction Note
Barbell: 2 sub-5% pivots + 3 chalk anchors. Your per-user variance seed ships a unique portfolio.`;

export default function AIAnalysis({ embedded = false, slateInfo = null }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState("leverage");
  // Async-run tracking: the Deep Focus endpoint now dispatches Claude to
  // a background task and returns immediately (fixes Cloudflare 524s on
  // 12-game slates). We poll /ai/last every 4s while `running=true` and
  // stop once it flips false — same fire-and-poll shape used by the
  // optimizer job endpoint. `startedAt` powers a "running for Xs" hint
  // so users know it's still working through a long slate, not stuck.
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  // A slate is "loaded" once the backend confirms non-zero players on
  // /api/slate/info. Until then we keep the old "Load DKSalaries" empty
  // state; once loaded we flip to a "ready — hit Run" pitch anchored
  // to the user's actual slate.
  const slateLoaded = !!(slateInfo && (slateInfo.n_players || 0) > 0);
  const slateSummary = slateLoaded
    ? `${slateInfo.n_players} players · ${slateInfo.n_games || 0} games`
    : null;

  // Hydrate on mount — pick up any in-progress or completed report so the
  // page survives a refresh mid-run.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.aiLast();
        if (r.report) setReport(r.report);
        if (r.running) {
          setLoading(true);
          setStartedAt(r.started_at || new Date().toISOString());
          if (r.focus) setFocus(r.focus);
        }
      } catch (err) {
        // Cached report may not exist yet on first visit — that's fine.
        console.debug("aiLast: no cached report yet", err?.message || err);
      }
    })();
  }, []);

  // Poll while an analysis is running. 4s interval balances "feels
  // responsive" against "don't hammer the DB". Auto-tears down on
  // completion (either success or error).
  useEffect(() => {
    if (!loading) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.aiLast();
        if (cancelled) return;
        if (!r.running) {
          setLoading(false);
          setStartedAt(null);
          setElapsedSec(0);
          if (r.error) {
            toast.error(r.error);
          } else if (r.report) {
            setReport(r.report);
            toast.success("AI attack plan generated");
          }
        } else if (r.started_at && !startedAt) {
          setStartedAt(r.started_at);
        }
      } catch (err) {
        console.debug("aiLast poll failed:", err?.message || err);
      }
    };
    const interval = setInterval(tick, 4000);
    // First tick immediately so we don't wait 4s for the first status.
    tick();
    // Elapsed-time ticker (separate interval so the UI updates smoothly
    // once a second even if the API polls slower).
    const clock = setInterval(() => {
      if (startedAt) {
        const s = Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 1000));
        setElapsedSec(s);
      }
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearInterval(clock);
    };
  }, [loading, startedAt]);

  const run = async () => {
    setLoading(true);
    setStartedAt(new Date().toISOString());
    setElapsedSec(0);
    try {
      const r = await api.aiAnalyze(focus);
      if (r?.status === "already_running") {
        toast.info("An analysis is already running for this slate — waiting for it to finish");
      } else {
        toast.success("AI analysis started — this can take 30-180 seconds");
      }
    } catch (e) {
      setLoading(false);
      setStartedAt(null);
      toast.error(e.response?.data?.detail || "Failed to start AI analysis");
    }
  };

  return (
    <section className={embedded ? "border-t border-zinc-800 pt-6" : ""}>
      {embedded && (
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <Brain size={16} className="text-[#39FF14]" weight="fill" />
            <div className="heading text-lg font-bold">Deep Focus Analysis</div>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 border border-zinc-800 px-1.5 py-0.5">
              Lens-tuned · complements the posture-tuned Attack Plan above
            </span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <aside className="lg:col-span-4 space-y-4">
        {/* Signals promoted ABOVE the fold — the audit flagged the empty right
            panel as revealing what the AI actually feeds on. Front-loading it. */}
        <div className="bg-[#0a0a0a] border border-[#39FF14]/40 p-4">
          <div className="overline mb-2 text-[#39FF14]">Signals Fed to Model</div>
          <ul className="text-xs text-zinc-300 space-y-1.5 list-disc list-inside">
            <li>Top-15 leverage plays (max_proj − mean_proj)</li>
            <li>Top-15 highest ownership consensus (chalk)</li>
            <li>QB stack pool ranked by market projection</li>
            <li>Volatility (σ) across projection sources</li>
            <li>Vegas ITT, spread, O/U per game</li>
            <li>Defense-vs-position ranks + pressure/OL</li>
          </ul>
        </div>

        <div className="bg-[#0a0a0a] border border-zinc-800 p-6 mm-ai-glow">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={22} className="text-[#39FF14]" weight="fill" />
            <div className="heading text-lg font-bold">AI Slate Brief</div>
          </div>
          <div className="overline mb-4">Slate Strategist · Claude Sonnet 5</div>
          <p className="text-sm text-zinc-300 mb-6">
            Reads WarPaint's DK-native projection model — plus any optional
            market-consensus overlay — and produces a tactical Milly Maker
            attack plan: chalk to fade, leverage pivots to attack, optimal
            stack combos, and roster construction notes.
          </p>

          <div className="overline mb-2">Focus</div>
          <div className="grid grid-cols-2 gap-1 mb-4">
            {FOCUS_MODES.map((f) => (
              <button
                key={f.key}
                data-testid={`ai-focus-${f.key}`}
                onClick={() => setFocus(f.key)}
                title={f.desc}
                className={`py-2 px-2 text-xs uppercase tracking-widest transition-colors border ${
                  focus === f.key
                    ? "bg-[#39FF14] text-black border-[#39FF14]"
                    : "text-zinc-300 border-zinc-800 hover:border-zinc-600"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] mono tracking-widest text-zinc-500 mb-4 uppercase">
            {FOCUS_MODES.find((f) => f.key === focus)?.desc}
          </div>

          <button
            data-testid={TID.aiRunBtn}
            onClick={run}
            disabled={loading}
            className="w-full bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-xs py-3 transition-colors inline-flex items-center justify-center gap-2"
          >
            <Sparkle size={14} weight={loading ? "fill" : "fill"} className={loading ? "animate-spin" : ""} />
            {loading ? `Analyzing… ${elapsedSec}s` : "Run AI Analysis"}
          </button>

          {loading && <div className="mt-4 h-1 mm-scan-bar" />}
          {loading && (
            <div className="mt-3 text-[10px] mono uppercase tracking-widest text-zinc-500 leading-relaxed">
              Claude Sonnet 5 is reading your 12-game slate — this typically takes
              <span className="text-[#39FF14] font-bold"> 30-180 seconds</span>. You can navigate
              away; the analysis keeps running server-side and will be waiting when you return.
            </div>
          )}
        </div>
      </aside>

      <main className="lg:col-span-8">
        <div
          data-testid={TID.aiReport}
          className="bg-[#121212] border border-zinc-800 p-6 min-h-[600px] relative"
        >
          {loading && !report && (
            <div
              data-testid="ai-analyzing-state"
              className="absolute inset-0 flex items-center justify-center bg-[#121212] px-6"
            >
              <div className="text-center max-w-md">
                <div className="inline-flex items-center gap-2 mb-4 border border-[#39FF14] bg-[#39FF14]/10 px-3 py-1.5">
                  <Sparkle size={12} weight="fill" className="text-[#39FF14] animate-pulse" />
                  <span className="mono text-[10px] tracking-widest text-[#39FF14] uppercase font-bold">
                    Analyzing · {elapsedSec}s elapsed
                  </span>
                </div>
                <div className="heading text-2xl font-black text-white mb-3 uppercase tracking-tighter">
                  Claude Sonnet 5 is reading your slate
                </div>
                <div className="text-zinc-400 text-sm leading-relaxed mb-6">
                  Feeding your {slateSummary || "slate"} through the contest rules,
                  ownership model, and Vegas game scripts. Plans typically deliver in{" "}
                  <span className="text-[#39FF14] font-bold">30-180 seconds</span> depending on
                  slate complexity.
                </div>
                {/* Segmented progress spinner made of scan-bars for a "system
                    is working" affordance. Real progress is elapsed time
                    (background task doesn't emit fine-grained progress). */}
                <div className="flex items-center justify-center gap-1 mb-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-1 mm-scan-bar"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
                <div className="mono text-[10px] tracking-widest text-zinc-600 uppercase">
                  Safe to navigate away — task runs server-side
                </div>
              </div>
            </div>
          )}
          {!report && !loading && (
            <>
              {/* Blurred sample so users can SEE what the AI produces before spending a run */}
              <div
                data-testid="ai-report-sample"
                className="pointer-events-none select-none"
                style={{ filter: "blur(3px) saturate(0.85)", opacity: 0.55 }}
                aria-hidden="true"
              >
                <div className="prose prose-invert max-w-none">
                  <ReportView text={SAMPLE_REPORT} />
                </div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-[#121212]/70 via-[#121212]/90 to-[#121212] px-6">
                <div className="text-center max-w-md">
                  <div
                    data-testid={slateLoaded ? "ai-slate-ready-pill" : "ai-slate-sample-pill"}
                    className={`inline-flex items-center gap-2 mb-3 border px-2 py-1 ${
                      slateLoaded
                        ? "border-[#39FF14] bg-[#39FF14]/10"
                        : "border-[#39FF14]/40 bg-black/60"
                    }`}
                  >
                    {slateLoaded ? (
                      <Sparkle size={10} weight="fill" className="text-[#39FF14]" />
                    ) : (
                      <Lock size={10} weight="fill" className="text-[#39FF14]" />
                    )}
                    <span className="mono text-[9px] tracking-widest text-[#39FF14] uppercase">
                      {slateLoaded ? `Ready · your slate · ${slateSummary}` : "Sample · not your slate"}
                    </span>
                  </div>
                  <div className="heading text-xl font-black text-white mb-2 uppercase tracking-tighter">
                    Draft your attack plan
                  </div>
                  <div className="text-zinc-300 text-sm mb-5 leading-relaxed">
                    {slateLoaded
                      ? "Pick a focus mode on the left and click Run. Claude Sonnet 5 reads YOUR slate — projections, ownership, Vegas lines, stack pool — and returns a plain-English plan in 30–90 seconds."
                      : "Load a slate from Slate Selection, pick a focus mode, click Run. Claude Sonnet 5 returns a plain-English Milly attack plan in 30–90 seconds — sample preview shown behind."}
                  </div>
                  <button
                    onClick={run}
                    className="inline-flex items-center gap-2 bg-[#39FF14] text-black font-black uppercase tracking-widest text-xs py-2.5 px-5 hover:bg-[#2ee30d] transition-colors"
                  >
                    <Sparkle size={12} weight="fill" />
                    Run AI Analysis
                  </button>
                </div>
              </div>
            </>
          )}
          {report && (
            <div className="prose prose-invert max-w-none">
              <ReportView text={report} />
            </div>
          )}
        </div>
      </main>
      </div>
    </section>
  );
}

function ReportView({ text }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-1 text-sm leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith("## ")) {
          return (
            <h3 key={i} className="heading text-xl font-bold text-[#39FF14] mt-6 mb-2 uppercase tracking-tight">
              {line.slice(3)}
            </h3>
          );
        }
        if (line.startsWith("# ")) {
          return (
            <h2 key={i} className="heading text-2xl font-black mt-6 mb-3">{line.slice(2)}</h2>
          );
        }
        if (line.startsWith("- ") || line.startsWith("* ")) {
          return (
            <div key={i} className="flex gap-2 pl-2">
              <span className="text-[#39FF14] mt-0.5">▸</span>
              <span className="text-zinc-300">{formatBold(line.slice(2))}</span>
            </div>
          );
        }
        if (/^\d+\./.test(line)) {
          return <div key={i} className="pl-2 text-zinc-300">{formatBold(line)}</div>;
        }
        if (line.trim() === "") return <div key={i} className="h-2" />;
        return <p key={i} className="text-zinc-300">{formatBold(line)}</p>;
      })}
    </div>
  );
}

function formatBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="text-white font-semibold">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}
